import ErrorIcon from '@mui/icons-material/Error'
import { Button, CircularProgress, useMediaQuery } from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import _, { filter, includes, isEmpty, isNull, some } from 'lodash'
import moment from 'moment/moment'
import { FC, useEffect, useMemo, useState } from 'react'
import { DocumentIconName } from '../../../../components/DocumentIconName'
import VoicePlayer from '../../../../components/RequestInput/VoicePlayer'
import { TextCollapse } from '../../../../components/TextCollapse/TextCollapse'
import { ReplyActor, ReplyContentType, ReplyDTO, ReplyStatus } from '../../../../swagger/dto'
import { theme } from '../../../../theme'
import { MessageWrapper } from '../styles'
import CitationsButton from './CitationsButton'
import { DateChip } from './DateChip'
import { ImageInRequestList } from './ImageInRequestList'
import { ScoreRating } from './ScoreRating'

import CalendarArrowUpIcon from '../../../../assets/icons/CalendarArrowUp.svg?react'
import { Reflections } from '../../../../components/Reflections'
import { useTextSelectionMenu } from '../../../../hooks/useTextSelectionMenu'
import { useRequestsStore } from '../../../../store/storeRequests'
import { ModelIcon } from '../../MainPage/components/ModelIcon'
import { ReplyMenu } from '../../RequestsPage/components/ReplyMenu'
import { getSuggestionDescriptor } from '../request.constants'
import { AnswerPreparationTimer } from './AnswerPreparationTimer'
import { AnswerVariantsControls } from './AnswerVariantsControls'
import MarkdownEditor from './MarkdownEditor'
import MessageInfoBlock from './MessageInfoBlock'
import RequestMessageError from './RequestMessageError'

type Props = {
    threadId: number
    isLastMessage?: boolean
    isFirstMessage?: boolean
    createdAtLast?: string
    request: ReplyDTO
    onChangeSuggestionText: (text: string) => void
}

export const Message: FC<Props> = ({
    threadId,
    request,
    isLastMessage,
    isFirstMessage,
    createdAtLast,
    onChangeSuggestionText,
}) => {
    const {
        actor,
        created_at,
        seconds_to_finish,
        is_scheduled,
        model_slug,
        actor_name,
        assistant_id,
        is_deleted,
        linked_replies,
    } = request

    const isActorUser = actor === ReplyActor.USER

    const answerVariants = useMemo<ReplyDTO[]>(
        () => (linked_replies?.length && !isActorUser ? [request, ...linked_replies] : [request]),
        [linked_replies, request],
    )

    const [currentAnswerVariantIndex, setCurrentAnswerVariantIndex] = useState(
        answerVariants.length - 1,
    )

    const { content, status, id, est_finish_at, finished_at, suggestion_content, score } =
        answerVariants.at(currentAnswerVariantIndex) ?? {}

    const isHasTable = () => {
        const contentText = filter(content, {
            content_type: 'TEXT',
        })
        return includes(contentText[0]?.text, '|-')
    }

    const isMobile = useMediaQuery(theme.breakpoints.down('md'))

    const { requestDetail, requestMessages } = useRequestsStore((state) => state)

    const { handleTextSelectionEnd } = useTextSelectionMenu()
    const hasRating = !isActorUser && status === ReplyStatus.FINISHED

    const isThinking = status === ReplyStatus.THINKING
    const isAnswering = status === ReplyStatus.ANSWER
    const isProcessAnswer =
        status !== ReplyStatus.FINISHED && status !== ReplyStatus.FAILED && !isActorUser
    const secondsFinish = seconds_to_finish || 0
    const imageContent = filter(
        content,
        ({ content_type, image_url }) => content_type === ReplyContentType.IMAGE && image_url,
    )
    const documentContent = filter(
        content,
        ({ content_type, file_url }) => content_type === ReplyContentType.DOCUMENT && file_url,
    )

    const documentReasoning = documentContent?.[0]?.reasoning

    const isAudio = some(content, ({ content_type }) =>
        includes([ReplyContentType.AUDIO_VOICE, ReplyContentType.AUDIO], content_type),
    )

    useEffect(() => {
        setCurrentAnswerVariantIndex(answerVariants.length - 1)
    }, [answerVariants, isLastMessage])

    return (
        <Stack
            id={`message-${id}`}
            sx={{
                pt: isFirstMessage ? 2 : 0,
            }}>
            <DateChip createdAt={created_at} createdAtLast={createdAtLast} />
            <MessageWrapper spacing={0} container>
                <Grid
                    sx={{
                        display: 'flex',
                        opacity: requestDetail?.is_archived || is_deleted ? '0.5' : 1,
                        width: '100%',
                        m: 0,
                        paddingRight: isActorUser ? 0 : '16px',
                        justifyContent: isActorUser ? 'flex-end' : 'flex-start',
                    }}>
                    <Stack
                        sx={{
                            overflowX: 'hidden',
                        }}
                        direction="row"
                        spacing={1}
                        width={isHasTable() ? "clamp(260px, 100%, 100%)" : "clamp(260px, 80%, 1000px)"}
                        justifyContent={isActorUser ? 'flex-end' : 'flex-start'}>
                        <Box
                            id={`reply_${actor}`}
                            sx={{
                                overflowX: 'hidden',
                                width:
                                    isProcessAnswer || isAudio
                                        ? '100%'
                                        : isMobile
                                          ? !isEmpty(documentContent)
                                              ? '100%'
                                              : 'auto'
                                          : 'auto',

                                display: 'flex',
                                gap: '8px',
                                flexDirection: 'column',
                            }}>
                            {!isActorUser && (
                                <Stack
                                    direction="row"
                                    sx={{
                                        alignItems: 'center',
                                        marginBottom: '4px',
                                    }}
                                    spacing={1}>
                                    <Stack
                                        spacing={1}
                                        direction="row"
                                        sx={{
                                            alignItems: 'center',
                                            borderRadius: '100px',
                                            border: '1px solid  #E7E8EE',
                                            padding: '2px 8px 2px 4px',
                                        }}>
                                        <Box
                                            sx={{
                                                textAlign: 'center',
                                            }}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    background: '#EBF5FD',
                                                    width: '20px',
                                                    height: '20px',
                                                    borderRadius: '50%',
                                                    position: 'relative',
                                                }}>
                                                <>
                                                    <img
                                                        style={{
                                                            width: '16px',
                                                        }}
                                                        src="/vector/logo13x13.svg"
                                                    />
                                                    {isProcessAnswer && (
                                                        <CircularProgress
                                                            sx={{
                                                                position: 'absolute',
                                                            }}
                                                            size={'20px'}
                                                        />
                                                    )}
                                                </>
                                            </Box>
                                        </Box>
                                        <Typography
                                            variant="caption2"
                                            sx={{
                                                color: '#9195A1',
                                            }}>
                                            {isProcessAnswer
                                                ? 'Генерирую ответ'
                                                : isNull(assistant_id)
                                                  ? `Arhiassistant`
                                                  : actor_name}
                                        </Typography>
                                    </Stack>

                                    {!isProcessAnswer &&
                                        isNull(assistant_id) &&
                                        !!actor_name &&
                                        model_slug !== 'AUTO' && (
                                            <Stack
                                                spacing={1}
                                                direction="row"
                                                sx={{
                                                    alignItems: 'center',
                                                    borderRadius: '100px',
                                                    border: '1px solid  #E7E8EE',
                                                    padding: '2px 8px 2px 4px',
                                                }}>
                                                <ModelIcon agentKey={model_slug} />
                                                <Typography
                                                    variant="caption2"
                                                    sx={{
                                                        color: '#9195A1',
                                                    }}>
                                                    {actor_name}
                                                </Typography>
                                            </Stack>
                                        )}

                                    {!isProcessAnswer && (
                                        <Typography
                                            variant="caption2"
                                            sx={{
                                                color: '#9195A1',
                                                marginLeft: 1,
                                            }}>
                                            {moment(finished_at ?? created_at).format('HH:mm')}
                                        </Typography>
                                    )}
                                </Stack>
                            )}

                            <Box
                                sx={{
                                    // display: 'grid',
                                    gridTemplateColumns: '1fr',
                                    gap: '16px',
                                    backgroundColor: isActorUser ? '#F4F4F6' : '',
                                    padding: isActorUser ? '8px 12px' : '',
                                    borderRadius: '20px',
                                }}>
                                {isProcessAnswer && !isAnswering && (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            my: 1,
                                        }}>
                                        <AnswerPreparationTimer estFinishAt={est_finish_at} />
                                        {!isMobile && <MessageInfoBlock />}
                                    </Box>
                                )}
                                {content.length === 0 && status === ReplyStatus.FAILED && (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            my: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}>
                                        <ErrorIcon color="error" sx={{ mr: 1 }} />
                                        Возникла ошибка!
                                    </Box>
                                )}
                                {_.map(content, (item) => {
                                    const { content_type } = item
                                    if (content_type === ReplyContentType.TEXT) {
                                        return (
                                            <Box
                                                key={`${content_type}_${id}`}
                                                onMouseUp={handleTextSelectionEnd}
                                                onTouchEnd={handleTextSelectionEnd}
                                                sx={{
                                                    position: 'relative',
                                                    wordBreak: 'break-all',
                                                    gridColumn: '1 / -1',
                                                }}>
                                                {(item.reasoning || isThinking) && (
                                                    <Reflections
                                                        isPending={isThinking}
                                                        reasoning={item.reasoning}
                                                    />
                                                )}
                                                <CitationsButton citations={item.citations} />
                                                <MarkdownEditor
                                                    contentEditableClassName="content-message"
                                                    citations={item.citations}
                                                    extraJsxComponentDescriptors={[
                                                        getSuggestionDescriptor(
                                                            onChangeSuggestionText,
                                                        ),
                                                    ]}
                                                    text={item.text?.replaceAll('---\n', '')}
                                                />
                                            </Box>
                                        )
                                    }

                                    if (
                                        content_type === ReplyContentType.AUDIO_VOICE &&
                                        item?.voice_url
                                    ) {
                                        return (
                                            <Box key={`${content_type}_${id}`}>
                                                <CitationsButton citations={item.citations} />
                                                <VoicePlayer
                                                    url={item.voice_url}
                                                    autoplay={
                                                        actor === ReplyActor.ASSISTANT &&
                                                        requestMessages?.at(0)?.id === id &&
                                                        secondsFinish > -30
                                                    }
                                                />

                                                {item.stenography && (
                                                    <>
                                                        <Typography
                                                            component="p"
                                                            sx={{
                                                                marginTop: '16px',
                                                                marginBottom: '12px',
                                                            }}
                                                            color="#9195A1"
                                                            variant="caption2">
                                                            Расшифровка сообщения
                                                        </Typography>
                                                        <Grid
                                                            onMouseUp={handleTextSelectionEnd}
                                                            onTouchEnd={handleTextSelectionEnd}
                                                            sx={{
                                                                border: '1px solid #E7E8EE',
                                                                borderRadius: '16px',
                                                                padding: '16px',
                                                                width: '100%',
                                                            }}>
                                                            <TextCollapse
                                                                maxLines={2}
                                                                text={
                                                                    <MarkdownEditor
                                                                        contentEditableClassName="content-message"
                                                                        citations={item.citations}
                                                                        extraJsxComponentDescriptors={[
                                                                            getSuggestionDescriptor(
                                                                                onChangeSuggestionText,
                                                                            ),
                                                                        ]}
                                                                        text={item.stenography}
                                                                    />
                                                                }
                                                            />
                                                        </Grid>
                                                    </>
                                                )}
                                            </Box>
                                        )
                                    }

                                    if (
                                        content_type === ReplyContentType.AUDIO &&
                                        item?.audio_url
                                    ) {
                                        return (
                                            <VoicePlayer
                                                key={`${content_type}_${id}`}
                                                url={item.audio_url}
                                            />
                                        )
                                    }

                                    if (
                                        content_type === ReplyContentType.VIDEO &&
                                        item?.video_url
                                    ) {
                                        return (
                                            <>
                                                <video
                                                    key={`${content_type}_${id}`}
                                                    style={{
                                                        borderRadius: '16px',
                                                    }}
                                                    width={isMobile ? '100%' : 560}
                                                    controls>
                                                    <source
                                                        src={item?.video_url}
                                                        type="video/mp4"
                                                    />
                                                </video>
                                                {isMobile && (
                                                    <Button
                                                        variant="outlined"
                                                        onClick={() => {
                                                            window.open(item?.video_url, '_blank')
                                                        }}>
                                                        Скачать видео
                                                    </Button>
                                                )}
                                            </>
                                        )
                                    }
                                    if (content_type === ReplyContentType.ERROR) {
                                        return (
                                            <RequestMessageError
                                                key={`${content_type}_${id}`}
                                                text={item?.text}
                                            />
                                        )
                                    }
                                })}
                                {isActorUser && (
                                    <Stack
                                        sx={{
                                            width: '100%',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                            gap: 1,
                                        }}>
                                        {is_scheduled && (
                                            <CalendarArrowUpIcon
                                                width={16}
                                                height={16}
                                                color="#006CFD"
                                            />
                                        )}
                                        <Typography
                                            variant="caption2"
                                            sx={{
                                                color: '#9195A1',
                                            }}>
                                            {moment(created_at).format('HH:mm')}
                                        </Typography>
                                    </Stack>
                                )}
                            </Box>

                            {!isEmpty(imageContent) && (
                                <ImageInRequestList actor={actor} imageContent={imageContent} />
                            )}

                            {!isEmpty(documentContent) && (
                                <Box
                                    sx={{
                                        position: 'relative',
                                        wordBreak: 'break-all',
                                        gridColumn: '1 / -1',
                                    }}>
                                    {documentReasoning && (
                                        <Reflections reasoning={documentReasoning} />
                                    )}
                                    <Box
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            gap: '16px',
                                            flexWrap: 'wrap',
                                            [theme.breakpoints.down('sm')]: {
                                                flexWrap: 'nowrap',
                                                flexDirection: 'column',
                                            },
                                        }}>
                                        {_.map(documentContent, ({ file_url }) => {
                                            const name = file_url.substring(
                                                file_url.lastIndexOf('/') + 1,
                                            )
                                            return (
                                                <DocumentIconName
                                                    key={file_url}
                                                    url={file_url}
                                                    name={name}
                                                />
                                            )
                                        })}
                                    </Box>
                                </Box>
                            )}
                            {!isActorUser &&
                                !isProcessAnswer &&
                                !requestDetail?.is_archived &&
                                status === ReplyStatus.FINISHED && (
                                    <Stack
                                        className="message-toolbar-container"
                                        direction="row"
                                        alignItems="center"
                                        justifyContent={isActorUser ? 'flex-end' : 'flex-start'}
                                        gap={1}>
                                        {answerVariants.length > 1 && isLastMessage && (
                                            <AnswerVariantsControls
                                                currentIndex={currentAnswerVariantIndex}
                                                total={answerVariants.length}
                                                onChange={setCurrentAnswerVariantIndex}
                                            />
                                        )}
                                        <ReplyMenu
                                            threadId={threadId}
                                            request={request}
                                            requestContent={content ?? []}
                                            totalScheduledReplyCount={
                                                requestDetail?.scheduled_reply_count ?? 0
                                            }
                                            isActorUser={isActorUser}
                                        />
                                        {hasRating && <ScoreRating score={score} replyId={id} />}
                                    </Stack>
                                )}

                            {suggestion_content && (
                                <MarkdownEditor
                                    contentEditableClassName="mdx-editor_suggestion"
                                    extraJsxComponentDescriptors={[
                                        getSuggestionDescriptor(onChangeSuggestionText),
                                    ]}
                                    text={suggestion_content}
                                />
                            )}
                        </Box>
                        {isActorUser && (
                            <Stack
                                className="message-toolbar-container"
                                direction="row"
                                alignItems="flex-end"
                                justifyContent="flex-end">
                                <ReplyMenu
                                    threadId={threadId}
                                    request={request}
                                    requestContent={content ?? []}
                                    totalScheduledReplyCount={
                                        requestDetail?.scheduled_reply_count ?? 0
                                    }
                                    isActorUser={isActorUser}
                                />
                            </Stack>
                        )}
                    </Stack>
                </Grid>
            </MessageWrapper>
        </Stack>
    )
}
